/* eslint-disable @typescript-eslint/no-explicit-any */
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '../auth/msalConfig';
import { isInShell } from '../common/Shell';

export const standaloneMsalInstance = new PublicClientApplication(msalConfig);

/**
 * Retrieves the MSAL (Microsoft Authentication Library) instance for the application.
 * 
 * This function checks if a MSAL instance is available from the Shell environment
 * (when running as inside marketplace). If found, it returns that instance to ensure
 * authentication state is shared across the application. Otherwise, it creates and
 * returns a new standalone MSAL instance.
 * 
 * @returns {PublicClientApplication} The MSAL PublicClientApplication instance,
 * either from the Shell environment or a newly created standalone instance.
 * 
 * @example
 * ```typescript
 * const msalInstance = getMsalInstance();
 * ```
 */
export const getMsalInstance = (): PublicClientApplication => {
  const shellMsal: PublicClientApplication = (window as any).shellMsalInstance;
  if (shellMsal && isInShell()) {
    return shellMsal;
  }

  return standaloneMsalInstance;
};

export const msalInstance = getMsalInstance();

let msalInitialized = false;
let msalInitializationPromise: Promise<void> | null = null;

/**
 * Initialize the MSAL instance
 * In @azure/msal-browser v3.x, the initialize() method MUST be called before using the instance.
 */
export const initializeMsalInstance = async (): Promise<void> => {
  if (msalInitialized) {
    return;
  }

  if (msalInitializationPromise) {
    return msalInitializationPromise;
  }

  msalInitializationPromise = (async () => {
    if ((window as any).shellMsalInstance && isInShell()) {
      msalInitialized = true;
      return;
    }
    await (msalInstance as any).initialize();
    msalInitialized = true;
  })();

  return msalInitializationPromise;
};

/**
 * Ensures MSAL instance is initialized before use
 * Throws error if initialization fails
 */
export const ensureMsalInitialized = async (): Promise<void> => {
  if (!msalInitialized) {
    await initializeMsalInstance();
  }
};